/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ChatShare.css';

const ChatShare = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userTimezone, setUserTimezone] = useState('');
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(timezone);
    loadSharedChat();
  }, [shareId]);

  const loadSharedChat = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/skillbot/shared/${shareId}`
      );
      setChatData(response.data);
    } catch (error) {
      console.error('Error loading shared chat:', error);
      setError(error.response?.data?.error || 'Failed to load shared conversation');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp, format = 'datetime') => {
    const date = new Date(timestamp);
    
    if (format === 'time') {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: userTimezone
      });
    }
    
    if (format === 'date') {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: userTimezone
      });
    }
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone
    });
  };

  const copyShareLink = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 3000);
    });
  };

  const exportAsText = () => {
    if (!chatData) return;

    let textContent = `SkillBot Conversation\n`;
    textContent += `Shared: ${formatTimestamp(chatData.sharedAt, 'datetime')}\n`;
    textContent += `Timezone: ${userTimezone}\n`;
    textContent += `${'='.repeat(60)}\n\n`;

    chatData.messages.forEach((message, index) => {
      const timestamp = formatTimestamp(message.timestamp, 'datetime');
      const role = message.type === 'user' ? 'You' : 'SkillBot';
      textContent += `[${timestamp}] ${role}:\n${message.text}\n\n`;
    });

    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skillbot-chat-${shareId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportAsPDF = async () => {
    if (!chatData) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/skillbot/shared/${shareId}/export-pdf`,
        { timezone: userTimezone },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `skillbot-chat-${shareId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="chat-share-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading shared conversation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-share-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>Unable to Load Conversation</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="home-button">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (!chatData) {
    return null;
  }

  return (
    <div className="chat-share-container">
      <div className="share-header">
        <div className="header-content">
          <div className="header-left">
            <h1>🤖 SkillBot Conversation</h1>
            <div className="header-meta">
              <span className="share-date">
                📅 {formatTimestamp(chatData.sharedAt, 'date')}
              </span>
              <span className="timezone-badge">
                🌍 {userTimezone}
              </span>
              <span className="message-count">
                💬 {chatData.messages.length} messages
              </span>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={copyShareLink} className="action-button copy-button">
              {showCopyNotification ? '✓ Copied!' : '🔗 Copy Link'}
            </button>
            <button onClick={exportAsText} className="action-button export-button">
              📄 Export Text
            </button>
            <button onClick={exportAsPDF} className="action-button export-button">
              📑 Export PDF
            </button>
          </div>
        </div>
      </div>

      <div className="share-content">
        <div className="messages-container">
          {chatData.messages.map((message, index) => (
            <div
              key={index}
              className={`shared-message ${message.type}-message`}
            >
              <div className="message-header">
                <div className="message-author">
                  {message.type === 'user' ? (
                    <>
                      <span className="author-icon">👤</span>
                      <span className="author-name">You</span>
                    </>
                  ) : (
                    <>
                      <span className="author-icon">🤖</span>
                      <span className="author-name">SkillBot</span>
                    </>
                  )}
                </div>
                <div className="message-timestamp">
                  {formatTimestamp(message.timestamp, 'datetime')}
                </div>
              </div>
              <div className="message-content">
                <div className="message-text">{message.text}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="share-footer">
          <div className="footer-content">
            <div className="skilldelivery-branding">
              <span className="brand-logo">SkillDelivery</span>
              <span className="brand-tagline">AI-Powered Career Development</span>
            </div>
            <div className="footer-actions">
              <button onClick={() => navigate('/skillbot')} className="cta-button">
                Start Your Own Conversation
              </button>
            </div>
          </div>
        </div>
      </div>

      {chatData.expiresAt && (
        <div className="expiry-notice">
          ⏰ This shared conversation will expire on{' '}
          {formatTimestamp(chatData.expiresAt, 'datetime')}
        </div>
      )}
    </div>
  );
};

export default ChatShare;
